import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  ContentCopy as DuplicateIcon,
  History as HistoryIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { protocolAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { downloadAsFile } from '../../utils/downloadAsFile';
import { toast } from 'react-toastify';
import DetailBreadcrumbs from '../common/DetailBreadcrumbs';

const ProtocolDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [protocol, setProtocol] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [versionHistory, setVersionHistory] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionDialog, setVersionDialog] = useState(false);
  const [versionLoading, setVersionLoading] = useState(false);

  const isEditor = ['admin', 'lab_manager', 'lab_technician'].includes(currentUser?.role);

  useEffect(() => {
    fetchProtocol();
    fetchExperiments();
    fetchVersions();
  }, [id]);

  const fetchProtocol = async () => {
    try {
      const response = await protocolAPI.getById(id);
      setProtocol(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching protocol:', err);
      setError('Failed to load protocol details');
    } finally {
      setLoading(false);
    }
  };

  const fetchExperiments = async () => {
    try {
      const response = await fetch(`/api/experiments?protocol_id=${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setExperiments(data.experiments || []);
      }
    } catch (err) {
      console.error('Error fetching experiments:', err);
    }
  };

  const fetchVersions = async () => {
    try {
      setVersionsLoading(true);
      const response = await fetch(`/api/protocols/${id}/versions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setVersionHistory(data.versions || []);

        // Find and set current version
        const current = data.versions?.find(v => v.is_current);
        setCurrentVersion(current);
      } else {
        console.warn('Could not fetch protocol versions');
      }
    } catch (err) {
      console.error('Error fetching protocol versions:', err);
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this protocol?')) {
      return;
    }

    try {
      await protocolAPI.delete(id);
      toast.success('Protocol deleted successfully');
      navigate('/protocols');
    } catch (err) {
      console.error('Error deleting protocol:', err);
      toast.error('Failed to delete protocol');
    }
  };

  const handlePreview = () => {
    if (protocol?.basic_steps) {
      setPreviewContent(protocol.basic_steps);
      setPreviewDialog(true);
    }
  };

  const handleDownload = async () => {
    if (!protocol?.document_path) {
      toast.error('No document available for download');
      return;
    }

    try {
      const response = await fetch(`/api/protocols/${id}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const filename = protocol.document_path.split('/').pop() || 'protocol.pdf';
      downloadAsFile(blob, filename);
      toast.success('Document downloaded successfully');
    } catch (err) {
      console.error('Error downloading document:', err);
      toast.error('Failed to download document');
    }
  };

  const handleDuplicate = () => {
    navigate(`/protocols/new?duplicate=${id}`);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewVersion = async (versionId) => {
    try {
      setVersionLoading(true);
      setVersionDialog(true);

      const response = await fetch(`/api/protocols/${id}/versions/${versionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch version details');
      }

      const data = await response.json();
      setSelectedVersion(data);
    } catch (err) {
      console.error('Error fetching version details:', err);
      toast.error('Failed to load version details');
      setVersionDialog(false);
    } finally {
      setVersionLoading(false);
    }
  };

  const handleCloseVersionDialog = () => {
    setVersionDialog(false);
    setSelectedVersion(null);
  };

  const handleRestoreVersion = async (versionId, versionNumber) => {
    if (!window.confirm(`Are you sure you want to restore version ${versionNumber}? This will create a new version based on the selected version.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/protocols/${id}/versions/${versionId}/restore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to restore version');
      }

      const result = await response.json();
      toast.success(`Protocol restored from version ${versionNumber}. New version ${result.new_version} created.`);

      // Refresh protocol and version history
      fetchProtocol();
      fetchVersions();
    } catch (err) {
      console.error('Error restoring version:', err);
      toast.error('Failed to restore version');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/protocols')}
          sx={{ mt: 2 }}
        >
          Back to Protocols
        </Button>
      </Box>
    );
  }

  if (!protocol) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Protocol not found</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/protocols')}
          sx={{ mt: 2 }}
        >
          Back to Protocols
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <DetailBreadcrumbs
        items={[
          { label: 'Protocols', to: '/protocols' },
          { label: protocol.name || 'Protocol' },
        ]}
      />
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            {protocol.name}
          </Typography>
          {currentVersion && (
            <Chip
              label={`Version ${currentVersion.version_number}`}
              color="primary"
              size="small"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Duplicate Protocol">
            <IconButton onClick={handleDuplicate} color="primary">
              <DuplicateIcon />
            </IconButton>
          </Tooltip>
          {isEditor && (
            <>
              <Button
                component={Link}
                to={`/protocols/${id}/edit`}
                variant="outlined"
                startIcon={<EditIcon />}
              >
                Edit
              </Button>
              <Button
                onClick={handleDelete}
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
              >
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Protocol Details" />
          <Tab label="Version History" icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab Panel 0: Protocol Details */}
      {tabValue === 0 && (
        <>
          {/* Basic Information */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Protocol ID
                </Typography>
                <Typography variant="body1">
                  {protocol.protocol_id}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Category
                </Typography>
                <Typography variant="body1">
                  {protocol.category || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body1">
                  {formatDate(protocol.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body1">
                  {formatDate(protocol.updated_at)}
                </Typography>
              </Grid>
              {protocol.version && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Version
                  </Typography>
                  <Typography variant="body1">
                    {protocol.version}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">
                  {protocol.description || 'No description provided'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Protocol Text */}
          {protocol.basic_steps && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Protocol Steps
                </Typography>
                <Button
                  startIcon={<ViewIcon />}
                  onClick={handlePreview}
                  variant="outlined"
                  size="small"
                >
                  Preview
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  maxHeight: '300px',
                  overflow: 'auto',
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                {protocol.basic_steps.substring(0, 500)}
                {protocol.basic_steps.length > 500 && '...'}
              </Typography>
            </Paper>
          )}

          {/* Document */}
          {protocol.document_path && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Document
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DocumentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1">
                    {protocol.document_path.split('/').pop()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Protocol document
                  </Typography>
                </Box>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  variant="contained"
                >
                  Download
                </Button>
              </Box>
            </Paper>
          )}

          {/* Reagents */}
          {protocol.required_reagents && protocol.required_reagents.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Required Reagents
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Reagent Name</TableCell>
                      <TableCell>Catalog Number</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {protocol.required_reagents.map((reagent, index) => (
                      <TableRow key={index}>
                        <TableCell>{reagent.name}</TableCell>
                        <TableCell>{reagent.catalog_number || 'N/A'}</TableCell>
                        <TableCell align="right">{reagent.quantity_per_sample}</TableCell>
                        <TableCell>{reagent.unit}</TableCell>
                        <TableCell>{reagent.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Usage Statistics */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Usage Statistics
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Times Used in Experiments
                </Typography>
                <Typography variant="h4" color="primary">
                  {experiments.length}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Recent Experiments */}
          {experiments.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recent Experiments Using This Protocol
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Experiment ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {experiments.slice(0, 5).map((experiment) => (
                      <TableRow key={experiment.id}>
                        <TableCell>
                          <Chip
                            label={experiment.experiment_id}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{experiment.name}</TableCell>
                        <TableCell>{formatDate(experiment.experiment_date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={experiment.status}
                            size="small"
                            color={
                              experiment.status === 'completed' ? 'success' :
                              experiment.status === 'in_progress' ? 'primary' :
                              'default'
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Experiment">
                            <IconButton
                              component={Link}
                              to={`/experiments/${experiment.id}`}
                              size="small"
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {experiments.length > 5 && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    component={Link}
                    to={`/experiments?protocol_id=${id}`}
                    variant="text"
                  >
                    View All {experiments.length} Experiments
                  </Button>
                </Box>
              )}
            </Paper>
          )}
        </>
      )}

      {/* Tab Panel 1: Version History */}
      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Version History
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {versionsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : versionHistory.length === 0 ? (
            <Alert severity="info">No version history available</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Version</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Change Summary</TableCell>
                    <TableCell align="center">Experiments</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versionHistory.map((version) => (
                    <TableRow
                      key={version.id}
                      sx={{
                        bgcolor: version.is_current ? 'action.selected' : 'inherit',
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={`v${version.version_number}`}
                            size="small"
                            color={version.is_current ? 'primary' : 'default'}
                          />
                          {version.is_current && (
                            <Chip
                              label="Current"
                              size="small"
                              color="success"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(version.created_at)}</TableCell>
                      <TableCell>{version.created_by_name || 'Unknown'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {version.change_summary || 'No summary provided'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          {version.experiment_count || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {version.is_archived ? (
                          <Chip label="Archived" size="small" color="default" />
                        ) : (
                          <Chip label="Active" size="small" color="success" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Version Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewVersion(version.id)}
                              color="primary"
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isEditor && !version.is_current && (
                            <Tooltip title="Restore this version">
                              <IconButton
                                size="small"
                                onClick={() => handleRestoreVersion(version.id, version.version_number)}
                                color="success"
                              >
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Protocol Steps Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Protocol Steps Preview</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              bgcolor: 'grey.50',
              p: 2,
              borderRadius: 1,
            }}
          >
            {previewContent}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Version Details Dialog */}
      <Dialog
        open={versionDialog}
        onClose={handleCloseVersionDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">
                Version {selectedVersion?.version_number} Details
              </Typography>
              {selectedVersion?.is_current && (
                <Chip label="Current Version" size="small" color="success" />
              )}
            </Box>
            {selectedVersion && (
              <Typography variant="caption" color="text.secondary">
                Created {formatDate(selectedVersion.created_at)} by {selectedVersion.created_by_name}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {versionLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : selectedVersion ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Basic Information */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Protocol Name
                    </Typography>
                    <Typography variant="body1">{selectedVersion.name}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body1">
                      {selectedVersion.category || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {selectedVersion.description || 'No description'}
                    </Typography>
                  </Grid>
                  {selectedVersion.change_summary && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Change Summary
                      </Typography>
                      <Typography variant="body1">{selectedVersion.change_summary}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Protocol Steps */}
              {selectedVersion.basic_steps && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Protocol Steps
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 400, overflow: 'auto' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                      }}
                    >
                      {selectedVersion.basic_steps}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Reagents */}
              {selectedVersion.required_reagents && selectedVersion.required_reagents.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Required Reagents
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Reagent Name</TableCell>
                          <TableCell>Catalog Number</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell>Unit</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedVersion.required_reagents.map((reagent, index) => (
                          <TableRow key={index}>
                            <TableCell>{reagent.name}</TableCell>
                            <TableCell>{reagent.catalog_number || 'N/A'}</TableCell>
                            <TableCell align="right">{reagent.quantity_per_sample}</TableCell>
                            <TableCell>{reagent.unit}</TableCell>
                            <TableCell>{reagent.notes || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Document Path */}
              {selectedVersion.document_path && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Document
                  </Typography>
                  <Typography variant="body2">
                    {selectedVersion.document_path}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="error">Failed to load version details</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVersionDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProtocolDetail;
